import { supabase } from '../supabase/client';

export const ModelCatalogStatus = Object.freeze({
  DRAFT: 'draft',
  ACTIVE: 'active',
  DISABLED: 'disabled',
  ARCHIVED: 'archived',
});

const VALID_STATUSES = new Set(Object.values(ModelCatalogStatus));

const MODEL_CATALOG_TABLE = 'model_catalog_models';
const MODEL_CATALOG_ADMIN_RPC = 'is_model_catalog_admin';
const MODEL_CATALOG_SELECT_COLUMNS = (
  'id, model_id, status, definition, created_by, updated_by, created_at, updated_at'
);

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function normalizeCatalogRow(row) {
  if (!isPlainObject(row)) return null;

  const modelId = typeof row.model_id === 'string' ? row.model_id.trim() : '';
  const status = typeof row.status === 'string' ? row.status.trim() : '';
  const definition = isPlainObject(row.definition) ? row.definition : null;

  if (!modelId || !VALID_STATUSES.has(status) || !definition) return null;

  if (typeof definition.id === 'string' && definition.id.trim() && definition.id.trim() !== modelId) {
    return null;
  }

  return {
    id: typeof row.id === 'string' ? row.id : null,
    modelId,
    status,
    definition,
    createdBy: typeof row.created_by === 'string' ? row.created_by : null,
    updatedBy: typeof row.updated_by === 'string' ? row.updated_by : null,
    createdAt: typeof row.created_at === 'string' ? row.created_at : null,
    updatedAt: typeof row.updated_at === 'string' ? row.updated_at : null,
  };
}

function normalizeCatalogRows(rows) {
  if (!Array.isArray(rows)) return [];
  const normalized = [];
  for (const row of rows) {
    const next = normalizeCatalogRow(row);
    if (next) normalized.push(next);
  }
  return normalized;
}

function assertClient(client) {
  if (!client) {
    throw new Error('[modelCatalogService] Supabase client is not configured.');
  }
}

export async function fetchActiveModelCatalogRows(client = supabase) {
  if (!client) return [];
  const { data, error } = await client
    .from(MODEL_CATALOG_TABLE)
    .select(MODEL_CATALOG_SELECT_COLUMNS)
    .eq('status', ModelCatalogStatus.ACTIVE);

  if (error) {
    throw new Error(`[modelCatalogService] Failed to fetch active catalog rows: ${error.message}`);
  }

  return normalizeCatalogRows(data);
}

export async function fetchModelCatalogRows(client = supabase) {
  assertClient(client);
  const { data, error } = await client
    .from(MODEL_CATALOG_TABLE)
    .select(MODEL_CATALOG_SELECT_COLUMNS)
    .order('updated_at', { ascending: false });

  if (error) {
    throw new Error(`[modelCatalogService] Failed to fetch catalog rows: ${error.message}`);
  }

  return normalizeCatalogRows(data);
}

export function extractActiveCatalogDefinitions(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row) => row && row.status === ModelCatalogStatus.ACTIVE && isPlainObject(row.definition))
    .map((row) => row.definition);
}

function assertStatus(status) {
  if (!VALID_STATUSES.has(status)) {
    throw new Error(`[modelCatalogService] Invalid status "${status}".`);
  }
}

function assertModelId(modelId) {
  if (typeof modelId !== 'string' || !modelId.trim()) {
    throw new Error('[modelCatalogService] modelId is required.');
  }
}

function assertDefinition(definition, modelId) {
  if (!isPlainObject(definition)) {
    throw new Error('[modelCatalogService] definition must be an object.');
  }
  if (typeof definition.id === 'string' && definition.id.trim() && definition.id.trim() !== modelId) {
    throw new Error(
      `[modelCatalogService] definition.id "${definition.id}" does not match modelId "${modelId}".`,
    );
  }
}

function requireNormalizedRow(row, action) {
  const normalized = normalizeCatalogRow(row);
  if (!normalized) {
    throw new Error(`[modelCatalogService] ${action} returned a malformed row.`);
  }
  return normalized;
}

export async function checkModelCatalogAdmin(client = supabase, userId = null) {
  if (!client || typeof client.rpc !== 'function') return false;

  try {
    let candidateUserId = typeof userId === 'string' && userId.trim() ? userId.trim() : null;

    if (!candidateUserId && typeof client.auth?.getUser === 'function') {
      const { data, error } = await client.auth.getUser();
      if (error) {
        console.warn('[modelCatalogService] getUser failed during admin check', error);
        return false;
      }
      candidateUserId = typeof data?.user?.id === 'string' && data.user.id.trim()
        ? data.user.id.trim()
        : null;
    }

    if (!candidateUserId) return false;

    const args = { candidate_user_id: candidateUserId };
    const { data, error } = await client.rpc(MODEL_CATALOG_ADMIN_RPC, args);

    if (error) {
      console.warn('[modelCatalogService] is_model_catalog_admin RPC failed', error);
      return false;
    }
    return data === true;
  } catch (error) {
    console.warn('[modelCatalogService] is_model_catalog_admin RPC threw', error);
    return false;
  }
}

export async function upsertModelCatalogRow(
  { modelId, status = ModelCatalogStatus.DRAFT, definition },
  client = supabase,
) {
  assertClient(client);
  assertModelId(modelId);
  assertStatus(status);
  assertDefinition(definition, modelId.trim());

  const trimmedModelId = modelId.trim();
  const payload = {
    model_id: trimmedModelId,
    status,
    definition,
  };

  const { data, error } = await client
    .from(MODEL_CATALOG_TABLE)
    .upsert(payload, { onConflict: 'model_id' })
    .select(MODEL_CATALOG_SELECT_COLUMNS)
    .single();

  if (error) {
    throw new Error(`[modelCatalogService] Failed to upsert catalog row: ${error.message}`);
  }

  return requireNormalizedRow(data, 'upsertModelCatalogRow');
}

export async function updateModelCatalogRowStatus(modelId, status, client = supabase) {
  assertClient(client);
  assertModelId(modelId);
  assertStatus(status);

  const { data, error } = await client
    .from(MODEL_CATALOG_TABLE)
    .update({ status })
    .eq('model_id', modelId.trim())
    .select(MODEL_CATALOG_SELECT_COLUMNS)
    .single();

  if (error) {
    throw new Error(
      `[modelCatalogService] Failed to update status for "${modelId}": ${error.message}`,
    );
  }

  return requireNormalizedRow(data, 'updateModelCatalogRowStatus');
}
